/** @format */

import { createSlice } from '@reduxjs/toolkit';
import axios from 'src/utils/axios';
import { ENDPOINTS } from 'src/constants';
import type { TagsPageResponse, TagsSearchResponse } from 'src/types';

const initialState = {
  tags: [],
  tagsLoading: false,
  tagsError: null,
  tagSearchQuery: '',
  tagSearchResults: [],
  tagSearchLoading: false,
  tagSearchError: null
};

const slice = createSlice({
  name: 'tags',
  initialState,
  reducers: {
    getTagsStart(state) {
      state.tagsLoading = true;
      state.tagsError = null;
    },
    getTagsSuccess(state, action) {
      state.tags = action.payload;
      state.tagsLoading = false;
    },
    getTagsFailure(state, action) {
      state.tagsError = action.payload;
      state.tagsLoading = false;
    },
    searchTagsStart(state, action) {
      state.tagSearchLoading = true;
      state.tagSearchError = null;
      state.tagSearchQuery = action.payload ?? '';
    },
    searchTagsSuccess(state, action) {
      state.tagSearchResults = action.payload;
      state.tagSearchLoading = false;
    },
    searchTagsFailure(state, action) {
      state.tagSearchError = action.payload;
      state.tagSearchLoading = false;
    },
    clearTagSearch(state) {
      state.tagSearchQuery = '';
      state.tagSearchResults = [];
      state.tagSearchError = null;
      state.tagSearchLoading = false;
    }
  }
});

export const reducer = slice.reducer;

function parseTagsPagePayload(data) {
  if (Array.isArray(data)) {
    return { tags: data, nextCursor: null };
  }
  const tags = Array.isArray(data?.tags) ? data.tags : [];
  const nextCursor = data?.nextCursor ?? null;
  return { tags, nextCursor };
}

async function fetchAllTagsPages() {
  let merged = [];
  let nextCursor = null;

  do {
    const params = new URLSearchParams();
    if (nextCursor) {
      params.set('cursor', nextCursor);
    }
    const qs = params.toString();
    const url = `${ENDPOINTS.GET_TAGS_ENDPOINT}${qs ? `?${qs}` : ''}`;
    const response = await axios.get<TagsPageResponse>(url);
    const { tags, nextCursor: cursor } = parseTagsPagePayload(response?.data);
    merged = [...merged, ...tags];
    nextCursor = cursor || null;
  } while (nextCursor);

  return merged;
}

function normalizeSearchPayload(data) {
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data?.tags)) {
    return data.tags;
  }
  return [];
}

function formatError(error) {
  return typeof error === 'string'
    ? error
    : error?.message || error?.error || 'Request failed';
}

export const getTags = () => async dispatch => {
  const { getTagsStart, getTagsSuccess, getTagsFailure } = slice.actions;

  try {
    dispatch(getTagsStart());
    const tags = await fetchAllTagsPages();
    dispatch(getTagsSuccess(tags));
  } catch (error) {
    dispatch(getTagsFailure(formatError(error)));
  }
};

export const searchTags = search => async dispatch => {
  const { searchTagsStart, searchTagsSuccess, searchTagsFailure } = slice.actions;
  const q = typeof search === 'string' ? search.trim() : '';

  try {
    dispatch(searchTagsStart(q));
    if (!q) {
      dispatch(searchTagsSuccess([]));
      return;
    }
    const params = new URLSearchParams({ search: q });
    const url = `${ENDPOINTS.SEARCH_TAGS_ENDPOINT}?${params.toString()}`;
    const response = await axios.get<TagsSearchResponse>(url);
    const results = normalizeSearchPayload(response?.data);
    dispatch(searchTagsSuccess(results));
  } catch (error) {
    dispatch(searchTagsFailure(formatError(error)));
  }
};

export const { clearTagSearch } = slice.actions;

export default slice;
